import LogoComponent from "./logo";
import { FACEBOOK_URL, INSTAGRAM_URL, TWITTER_URL } from "../constants/links";
import { BsEnvelope, BsFacebook, BsGeoAlt, BsInstagram, BsTelephone, BsTwitter, BsWhatsapp } from "react-icons/bs";

function FooterColumn({ title, children }) {
  return (
    <div>
      <h2 className="text-sm uppercase tracking-widest text-purple-800 mb-4">{title}</h2>
      {children}
    </div>
  );
}

function FooterLinks({ links }) {
  return (
    <ul className="space-y-2.5">
      {links.map((link) => (
        <li key={link.href}>
          <a href={link.href} className="text-sm text-gray-600 hover:text-purple-700 transition-colors">
            {link.label}
          </a>
        </li>
      ))}
    </ul>
  );
}

export const Footer = () => {
  const socialLinks = [
    { label: "Parcelin on Facebook", href: FACEBOOK_URL, icon: <BsFacebook /> },
    { label: "Parcelin on X (Twitter)", href: TWITTER_URL, icon: <BsTwitter /> },
    { label: "Parcelin on Instagram", href: INSTAGRAM_URL, icon: <BsInstagram /> },
  ];

  return (
    <footer className="w-full bg-white border-t border-gray-100">

      <div className="max-w-6xl mx-auto px-4 sm:px-5 py-12 md:py-14 grid grid-cols-2 xl:grid-cols-12 gap-x-6 gap-y-10">
        <div className="col-span-2 xl:col-span-3">
          <a href="/" className="inline-block" aria-label="Parcelin home">
            <LogoComponent placement="footer" />
          </a>
          <p className="mt-3 text-base font-semibold text-gray-900">Parcel Delivery, Done Right</p>
          <p className="mt-2 text-sm text-gray-600 max-w-xs">
            Affordable, eco-friendly parcel delivery across Gauteng and Limpopo, using vehicles already on the road.
          </p>
          <ul className="mt-5 flex gap-2" aria-label="Social media">
            {socialLinks.map((social) => (
              <li key={social.href}>
                <a
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-50 text-purple-700 text-lg hover:bg-purple-600 hover:text-white transition-colors"
                >
                  {social.icon}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <nav aria-label="Footer" className="col-span-2 xl:col-span-3 grid grid-cols-2 gap-6">
          <FooterColumn title="Send">
            <FooterLinks
              links={[
                { href: "/", label: "Get a Quote" },
                { href: "/track-parcel", label: "Track a Parcel" },
                { href: "/how-it-works", label: "How It Works" },
                { href: "/faq", label: "FAQ" },
              ]}
            />
          </FooterColumn>
          <FooterColumn title="Company">
            <FooterLinks
              links={[
                { href: "/about-us", label: "About Us" },
                { href: "/contact", label: "Contact" },
                { href: "/signup", label: "Business Accounts" },
                { href: "/developers", label: "API Docs" },
              ]}
            />
          </FooterColumn>
        </nav>

        <div className="col-span-2 xl:col-span-6 grid grid-cols-1 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-6">
          <FooterColumn title="Get in Touch">
            <ul className="space-y-2.5 text-sm">
              <li>
                <a href="tel:+27699042846" className="inline-flex items-center gap-2 text-gray-600 hover:text-purple-700 transition-colors">
                  <BsTelephone className="text-purple-600 flex-shrink-0" aria-hidden="true" /> 069 904 2846
                </a>
              </li>
              <li>
                <a href="https://wa.me/27699042846" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-gray-600 hover:text-purple-700 transition-colors">
                  <BsWhatsapp className="text-purple-600 flex-shrink-0" aria-hidden="true" /> WhatsApp Us
                </a>
              </li>
              <li>
                <a href="mailto:support@parcelin.co.za" className="inline-flex items-center gap-2 text-gray-600 hover:text-purple-700 transition-colors whitespace-nowrap">
                  <BsEnvelope className="text-purple-600 flex-shrink-0" aria-hidden="true" /> support@parcelin.co.za
                </a>
              </li>
            </ul>
          </FooterColumn>
          <FooterColumn title="Our Offices">
            <ul className="space-y-3 text-sm">
              {[
                { name: "Midrand", lines: ["Gallagher Business Exchange", "688 Gallagher Ave, Halfway House, 1685"] },
                { name: "Giyani", lines: ["Bafana Hardware Building, Old FNB Road", "Giyani CBD (behind SupaQuick)"] },
              ].map((office) => (
                <li key={office.name} className="flex items-start gap-2">
                  <BsGeoAlt className="text-purple-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <address className="not-italic leading-snug">
                    <span className="block font-semibold text-gray-900">{office.name}</span>
                    {office.lines.map((line) => (
                      <span key={line} className="block text-gray-600">{line}</span>
                    ))}
                  </address>
                </li>
              ))}
              <li>
                <a href="/contact" className="text-sm font-medium text-purple-700 hover:text-purple-900 transition-colors">
                  Addresses & Directions →
                </a>
              </li>
            </ul>
          </FooterColumn>
        </div>
      </div>

      <div className="border-t border-gray-100 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-5 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-500">
          <p>© {new Date().getFullYear()} Earlybirds Corporation (PTY) Ltd. All rights reserved.</p>
          <ul className="flex gap-5">
            <li><a href="/terms" className="hover:text-purple-700 transition-colors">Terms of Service</a></li>
            <li><a href="/privacy" className="hover:text-purple-700 transition-colors">Privacy Policy</a></li>
          </ul>
        </div>
      </div>
    </footer>
  );
};
